import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, Zap, BarChart3, Bot, CheckCircle, ChevronRight, 
  Globe, Lock, Eye, TrendingUp, Users, ArrowRight, Play,
  Smartphone, Monitor, Chrome, Star, Quote, Menu, X,
  Sparkles, Target, Activity, Fingerprint, ShieldCheck, Layers
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Platform data — real logos via simpleicons.org CDN (grayscale for premium look)
const LOGO_COLOR = '9ca3af'; // Tailwind gray-400
const platforms = [
  { name: 'Google Ads', slug: 'google' },
  { name: 'Facebook', slug: 'facebook' },
  { name: 'TikTok', slug: 'tiktok' },
  { name: 'Instagram', slug: 'instagram' },
  { name: 'Twitter/X', slug: 'x' },
  { name: 'YouTube', slug: 'youtube' },
  { name: 'Snapchat', slug: 'snapchat' },
  { name: 'Pinterest', slug: 'pinterest' },
  { name: 'Reddit', slug: 'reddit' },
  { name: 'Twitch', slug: 'twitch' },
  { name: 'Quora', slug: 'quora' },
  { name: 'Spotify', slug: 'spotify' },
  { name: 'Discord', slug: 'discord' },
  { name: 'Telegram', slug: 'telegram' },
  { name: 'WhatsApp', slug: 'whatsapp' },
];

const features = [
  {
    icon: Shield,
    title: 'Bulletproof Cloaking',
    description: 'Cloak all major ad platforms with military-grade protection. Your campaigns stay invisible 24/7.',
    color: '#F59E0B',
    gradient: 'from-amber-500/20 to-orange-500/5'
  },
  {
    icon: Bot,
    title: 'AI Bot Detection',
    description: 'VLA™ machine learning blocks crawlers, verification bots, and Google Safe Browsing instantly.',
    color: '#22C55E',
    gradient: 'from-green-500/20 to-emerald-500/5'
  },
  {
    icon: Eye,
    title: 'Spy Tool Blocker',
    description: 'Block AdSpy, AdPlexity, and 13+ spy services. Your strategies remain completely hidden.',
    color: '#3B82F6',
    gradient: 'from-blue-500/20 to-cyan-500/5'
  },
  {
    icon: Zap,
    title: 'Fraud Shield',
    description: 'Stop click farms, headless browsers, and automated fraud before they drain your budget.',
    color: '#EF4444',
    gradient: 'from-red-500/20 to-orange-500/5'
  },
  {
    icon: Sparkles,
    title: 'AI Safe Pages',
    description: 'Generate compliant landing pages in 18 languages. Pass reviews, protect accounts.',
    color: '#8B5CF6',
    gradient: 'from-purple-500/20 to-violet-500/5'
  },
  {
    icon: BarChart3,
    title: 'Real-time Intel',
    description: 'Track every click with VLA™ scoring. See exactly who visits and why they were blocked.',
    color: '#EC4899',
    gradient: 'from-pink-500/20 to-rose-500/5'
  }
];

const stats = [
  { value: 99.9, suffix: '%', label: 'Detection Rate', icon: Target },
  { value: 7.8, suffix: 'B+', label: 'IPs in Database', icon: Globe },
  { value: 21, suffix: '+', label: 'Ad Networks', icon: Layers },
  { value: 100, suffix: 'ms', prefix: '<', label: 'Redirect Speed', icon: Zap }
];

const testimonials = [
  {
    name: 'Alex M.',
    role: 'Media Buyer',
    content: 'Finally a cloaker that actually works. My accounts have been running for months without any bans.',
    rating: 5,
    revenue: '$45K/mo'
  },
  {
    name: 'Sarah K.',
    role: 'Affiliate Marketer',
    content: 'The AI safe page generator alone is worth it. Saves me hours every week.',
    rating: 5,
    revenue: '$28K/mo'
  },
  {
    name: 'Mike R.',
    role: 'Agency Owner',
    content: 'Switched from Adspect. Better detection, faster redirects, cleaner interface.',
    rating: 5,
    revenue: '$120K/mo'
  }
];

const faqs = [
  {
    q: 'What is cloaking?',
    a: 'Cloaking shows different content to different visitors. Bots and reviewers see a safe page while real users see your offer.'
  },
  {
    q: 'Which platforms do you support?',
    a: 'All major platforms: Google Ads, Facebook, TikTok, Instagram, Twitter/X, Bing, Snapchat, YouTube, LinkedIn, Taboola, Outbrain, and more.'
  },
  {
    q: 'How does VLA™ bot detection work?',
    a: 'Our AI analyzes 2200+ fingerprint signals, IP intelligence from 7.8B+ records, and behavioral patterns to detect bots with 99.9% accuracy.'
  },
  {
    q: 'Do I need coding knowledge?',
    a: 'No! Add our PHP filter or use our WordPress plugin. Setup takes under 5 minutes.'
  },
  {
    q: 'Is there a click limit?',
    a: 'No limits. All plans include unlimited clicks at a flat monthly rate.'
  },
  {
    q: 'Do you accept crypto?',
    a: 'Yes. Bitcoin, Ethereum, USDT (TRC20 & ERC20), and other major cryptocurrencies accepted.'
  }
];

// Animated counter hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
    }
  }, [startOnView]);

  useEffect(() => {
    if (!hasStarted) return;
    
    let startTime;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [hasStarted, end, duration]);

  useEffect(() => {
    if (!startOnView) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startOnView, hasStarted]);

  return { count, ref };
};

// Stat Card Component - simplified version without animation
const StatCard = ({ stat }) => {
  return (
    <div 
      className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/[0.05] hover:border-[#F59E0B]/20 transition-all duration-300 hover:bg-white/[0.03]"
    >
      <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
        <stat.icon className="w-12 h-12 text-[#F59E0B]" />
      </div>
      <div className="text-4xl md:text-5xl font-bold text-white mb-2">
        {stat.prefix || ''}{stat.value}{stat.suffix}
      </div>
      <div className="text-sm text-gray-500 uppercase tracking-wider">{stat.label}</div>
    </div>
  );
};

// Floating particles component
const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-[#F59E0B]/30 rounded-full animate-float"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${15 + Math.random() * 10}s`
          }}
        />
      ))}
    </div>
  );
};

export default function ProfessionalLanding() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Track mouse for spotlight effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-x-hidden">
      {/* Global Styles for Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
          50% { transform: translateY(-20px) translateX(10px); opacity: 0.6; }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(212, 175, 55, 0.3); }
          50% { box-shadow: 0 0 40px rgba(212, 175, 55, 0.6); }
        }
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-float { animation: float linear infinite; }
        .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .animate-gradient { 
          background-size: 200% 200%;
          animation: gradient-shift 8s ease infinite; 
        }
        .animate-slide-up { animation: slide-up 0.6s ease-out forwards; }
        .noise-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%' height='100%' filter='url(%23noise)'/%3E%3C/svg%3E");
          opacity: 0.03;
        }
      `}</style>

      {/* Noise Texture Overlay */}
      <div className="fixed inset-0 noise-overlay pointer-events-none z-[1]" />

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#030303]/80 backdrop-blur-2xl border-b border-white/[0.03]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-18 py-3">
            <div className="flex items-center gap-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-[#F59E0B]/30 rounded-xl blur-lg group-hover:blur-xl transition-all" />
                <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center shadow-lg">
                  <Shield className="w-6 h-6 text-black" />
                </div>
              </div>
              <span className="text-xl font-bold tracking-tight">CloakItAura</span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              <a href="#features" className="px-4 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">Features</a>
              <a href="#pricing" className="px-4 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">Pricing</a>
              <a href="#faq" className="px-4 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">FAQ</a>
              <Link to="/blog" className="px-4 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all">Blog</Link>
              <Link to="/login" className="ml-2" data-testid="nav-sign-in-btn">
                <Button className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black font-semibold hover:shadow-lg hover:shadow-[#F59E0B]/20 transition-all px-6">
                  Sign In
                </Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 rounded-lg hover:bg-white/5 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#030303]/98 backdrop-blur-2xl border-t border-white/5 p-6 space-y-2">
            <a href="#features" className="block text-gray-400 hover:text-white py-3 px-4 rounded-lg hover:bg-white/5 transition-all">Features</a>
            <a href="#pricing" className="block text-gray-400 hover:text-white py-3 px-4 rounded-lg hover:bg-white/5 transition-all">Pricing</a>
            <a href="#faq" className="block text-gray-400 hover:text-white py-3 px-4 rounded-lg hover:bg-white/5 transition-all">FAQ</a>
            <Link to="/blog" className="block text-gray-400 hover:text-white py-3 px-4 rounded-lg hover:bg-white/5 transition-all">Blog</Link>
            <Link to="/login" className="block pt-2">
              <Button className="w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black font-semibold py-6">
                Sign In
              </Button>
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-36 pb-24 px-4 relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Animated Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/8 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#F59E0B]/5 rounded-full blur-[100px]" />
          <div className="absolute top-1/3 right-1/3 w-[300px] h-[300px] bg-amber-500/5 rounded-full blur-[80px]" />
        </div>
        
        {/* Floating Particles */}
        <FloatingParticles />
        
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: 'linear-gradient(rgba(212, 175, 55, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(212, 175, 55, 0.3) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
        />
        
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-w-0">
            {/* Left Content */}
            <div className="text-left min-w-0 w-full">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                <div className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse" />
                <span className="text-sm text-[#F59E0B]/90 font-medium tracking-wide">VLA™ AI-Powered Protection</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-[1.1] animate-slide-up" style={{ animationDelay: '0.2s' }}>
                <span className="block text-white">Stop Bans.</span>
                <span className="block bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F59E0B] bg-clip-text text-transparent animate-gradient">
                  Scale Faster.
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-400 max-w-xl mb-10 leading-relaxed animate-slide-up" style={{ animationDelay: '0.3s' }}>
                Military-grade cloaking for Google, Facebook, TikTok & 21+ platforms. 
                <span className="text-white"> 7.8B+ IP database</span>. 
                <span className="text-white"> 99.9% bot detection</span>. 
                Zero account bans.
              </p>

              <div className="flex justify-center sm:justify-start mb-12 animate-slide-up" style={{ animationDelay: '0.4s' }}>
                <Link to="/pricing">
                  <Button size="lg" className="group bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black font-bold text-lg px-10 py-7 rounded-xl hover:shadow-2xl hover:shadow-[#F59E0B]/30 transition-all duration-300 animate-pulse-glow">
                    Get Started
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>

              {/* Proven Campaigns On — Infinite Marquee */}
              <div className="animate-slide-up w-full min-w-0" style={{ animationDelay: '0.5s' }} data-testid="proven-campaigns-marquee">
                <p className="text-xs text-gray-600 mb-5 uppercase tracking-[0.2em]">Proven campaigns on</p>
                <div className="relative overflow-hidden w-full [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
                  <div className="flex w-max animate-logo-scroll gap-12 items-center">
                    {[...platforms, ...platforms].map((platform, idx) => (
                      <div
                        key={`${platform.slug}-${idx}`}
                        className="flex-shrink-0 flex items-center justify-center h-10 w-28 opacity-60 hover:opacity-100 transition-opacity duration-300"
                        title={platform.name}
                      >
                        <img
                          src={`https://cdn.simpleicons.org/${platform.slug}/${LOGO_COLOR}`}
                          alt={platform.name}
                          className="max-h-8 max-w-full object-contain"
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.innerHTML = `<span class='text-sm text-gray-500 font-medium tracking-wide'>${platform.name}</span>`; }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Visual Element */}
            <div className="hidden lg:block relative">
              <div className="relative">
                {/* Glowing orb */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px]">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#F59E0B]/20 to-amber-600/10 rounded-full blur-3xl animate-pulse" />
                  <div className="absolute inset-8 bg-gradient-to-br from-[#F59E0B]/30 to-transparent rounded-full blur-2xl" />
                </div>
                
                {/* Shield visual */}
                <div className="relative z-10 flex items-center justify-center h-[500px]">
                  <div className="relative">
                    <div className="absolute inset-0 bg-[#F59E0B]/20 rounded-3xl blur-2xl" />
                    <div className="relative w-64 h-64 rounded-3xl bg-gradient-to-br from-[#F59E0B]/10 to-transparent border border-[#F59E0B]/20 backdrop-blur-xl flex items-center justify-center">
                      <Shield className="w-32 h-32 text-[#F59E0B]/80" />
                    </div>
                  </div>
                  
                  {/* Floating stats cards */}
                  <div className="absolute -top-4 -right-4 px-4 py-3 rounded-xl bg-[#0a0a0a]/90 border border-white/10 backdrop-blur-xl">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      <span className="text-sm text-gray-400">Bot Blocked</span>
                    </div>
                    <div className="text-lg font-bold text-white mt-1">AdSpy Crawler</div>
                  </div>
                  
                  <div className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl bg-[#0a0a0a]/90 border border-white/10 backdrop-blur-xl">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-[#F59E0B]" />
                      <span className="text-sm text-gray-400">VLA™ Score</span>
                    </div>
                    <div className="text-lg font-bold text-green-400 mt-1">Safe: 12/100</div>
                  </div>
                  
                  <div className="absolute top-1/2 -right-8 px-4 py-3 rounded-xl bg-[#0a0a0a]/90 border border-white/10 backdrop-blur-xl">
                    <div className="text-2xl font-bold text-[#F59E0B]">7.8B+</div>
                    <div className="text-xs text-gray-500">IPs Tracked</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#F59E0B]/[0.02] via-transparent to-[#F59E0B]/[0.02]" />
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <StatCard key={stat.label} stat={stat} />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-28 px-4 relative">
        <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-[#F59E0B]/[0.03] rounded-full blur-[150px] -translate-y-1/2" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.05] mb-6">
              <Fingerprint className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-gray-400">Enterprise-Grade Protection</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Everything to
              <span className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] bg-clip-text text-transparent"> Dominate</span>
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              Military-grade cloaking technology trusted by 10,000+ media buyers worldwide
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className={`group relative p-7 rounded-2xl bg-gradient-to-br ${feature.gradient} border border-white/[0.05] hover:border-[#F59E0B]/20 transition-all duration-500 hover:-translate-y-1`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                
                <div 
                  className="relative w-14 h-14 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${feature.color}15` }}
                >
                  <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
                </div>
                
                <h3 className="relative text-xl font-semibold mb-3 text-white">{feature.title}</h3>
                <p className="relative text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-28 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#F59E0B]/[0.02] via-transparent to-[#F59E0B]/[0.02]" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.05] mb-6">
              <Users className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-gray-400">Trusted by Professionals</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Real <span className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] bg-clip-text text-transparent">Results</span>
            </h2>
            <p className="text-lg text-gray-500">Join thousands of successful media buyers</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div 
                key={i}
                className="group relative p-8 rounded-2xl bg-white/[0.02] border border-white/[0.05] hover:border-[#F59E0B]/20 transition-all duration-300"
              >
                <div className="absolute top-6 right-6">
                  <Quote className="w-8 h-8 text-[#F59E0B]/20" />
                </div>
                
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-5 h-5 fill-[#F59E0B] text-[#F59E0B]" />
                  ))}
                </div>
                
                <p className="text-gray-300 text-lg leading-relaxed mb-6">"{t.content}"</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center text-black font-bold text-lg">
                      {t.name[0]}
                    </div>
                    <div>
                      <div className="font-semibold text-white">{t.name}</div>
                      <div className="text-sm text-gray-500">{t.role}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Revenue</div>
                    <div className="text-[#F59E0B] font-semibold">{t.revenue}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-28 px-4 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#F59E0B]/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#F59E0B]/[0.02] rounded-full blur-[120px]" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/[0.05] mb-6">
              <TrendingUp className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-sm text-gray-400">Unlimited Clicks on All Plans</span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Simple <span className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] bg-clip-text text-transparent">Pricing</span>
            </h2>
            <p className="text-lg text-gray-500 mb-6">No hidden fees. No click limits. Cancel anytime.</p>
            <div className="flex items-center justify-center gap-4 text-gray-600">
              <span className="text-xl">₿</span>
              <span className="text-xl">Ξ</span>
              <span className="text-xl">₮</span>
              <span className="text-sm">Crypto accepted</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
            {/* Anti-fraud Plan */}
            <div className="group relative p-8 rounded-3xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/[0.08] hover:border-blue-500/30 transition-all duration-500 hover:-translate-y-2 flex flex-col">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              <div className="relative flex flex-col flex-grow">
                {/* Plan Icon */}
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 flex items-center justify-center mb-6">
                  <Zap className="w-7 h-7 text-blue-400" />
                </div>
                
                <h3 className="text-2xl font-bold mb-1 text-white">Anti-fraud</h3>
                <p className="text-sm text-gray-500 mb-6">Click fraud protection essentials</p>
                
                <div className="flex items-baseline gap-1 mb-8">
                  <span className="text-5xl font-bold text-white">$149</span>
                  <span className="text-gray-500">/mo</span>
                </div>
                
                <ul className="space-y-3 mb-8 flex-grow">
                  {[
                    { text: '10 Active Campaigns', highlight: true },
                    { text: 'Unlimited Clicks', highlight: true },
                    { text: 'Bot & Crawler Detection', highlight: false },
                    { text: 'Click Fraud Protection', highlight: false },
                    { text: 'Basic Datacenter & VPN Blocking', highlight: false },
                    { text: 'GeoIP Filtering (Country / Region)', highlight: false },
                    { text: 'Device Targeting (Mobile / Desktop)', highlight: false },
                    { text: 'Empty Referrer Blocking', highlight: false },
                    { text: 'Browser Fingerprinting (JS Signals)', highlight: false },
                    { text: 'Daily-Updated IP Threat Feed', highlight: false },
                    { text: 'Real-Time Visitor Logs', highlight: false },
                    { text: 'Detailed Analytics Dashboard', highlight: false },
                    { text: '6-Month Data Retention', highlight: false },
                    { text: 'Email Support', highlight: false }
                  ].map((f) => (
                    <li key={f.text} className="flex items-center gap-3 text-gray-300">
                      <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-3.5 h-3.5 text-blue-400" />
                      </div>
                      <span className={`text-sm ${f.highlight ? 'text-white font-medium' : ''}`}>{f.text}</span>
                    </li>
                  ))}
                </ul>
                
                <Link to="/pricing" className="block mt-auto">
                  <Button className="w-full py-6 rounded-xl font-semibold bg-white/[0.05] hover:bg-blue-500/20 border border-white/[0.1] hover:border-blue-500/30 transition-all text-white">
                    Get Started
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Personal Plan - Featured */}
            <div className="group relative p-8 rounded-3xl bg-gradient-to-b from-[#F59E0B]/15 to-[#F59E0B]/[0.02] border-2 border-[#F59E0B]/40 shadow-2xl shadow-[#F59E0B]/20 transition-all duration-500 hover:-translate-y-2 scale-105 z-10 flex flex-col">
              {/* Glow Effect */}
              <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-b from-[#F59E0B]/30 to-transparent blur-sm -z-10" />
              
              {/* Popular Badge */}
              <div className="absolute -top-5 left-1/2 -translate-x-1/2">
                <div className="px-6 py-2 bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black text-sm font-bold rounded-full shadow-xl shadow-[#F59E0B]/30 flex items-center gap-2">
                  <Star className="w-4 h-4 fill-black" />
                  Most Popular
                </div>
              </div>
              
              <div className="relative pt-4 flex flex-col flex-grow">
                {/* Plan Icon */}
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F59E0B]/30 to-[#D97706]/20 flex items-center justify-center mb-6 ring-2 ring-[#F59E0B]/20">
                  <Shield className="w-7 h-7 text-[#F59E0B]" />
                </div>
                
                <h3 className="text-2xl font-bold mb-1 text-white">Personal</h3>
                <p className="text-sm text-[#F59E0B]/80 mb-6">Full cloaking for solo marketers</p>
                
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-5xl font-bold text-white">$349</span>
                  <span className="text-gray-400">/mo</span>
                </div>
                <p className="text-xs text-green-400 mb-8 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Best value for beginners
                </p>
                
                <ul className="space-y-3 mb-8 flex-grow">
                  {[
                    { text: 'Everything in Anti-Fraud +', highlight: true },
                    { text: '50 Active Campaigns', highlight: true },
                    { text: 'Full Cloaking (21+ Ad Networks)', highlight: true },
                    { text: 'AI Safe Landing Pages (18 Languages)', highlight: true },
                    { text: 'VLA™ Machine-Learning Classifier', highlight: true },
                    { text: 'VLA Cohort Auto-Flagging', highlight: false },
                    { text: 'Client Hints Check (Sec-CH-UA)', highlight: false },
                    { text: 'WebRTC IP Leak Detection', highlight: false },
                    { text: 'JA4H TLS Cipher Fingerprint', highlight: false },
                    { text: 'HTTP/2 SETTINGS Fingerprint', highlight: false },
                    { text: '2,200+ Browser Fingerprint Signals', highlight: false },
                    { text: 'Headless Browser Detection', highlight: false },
                    { text: 'Behavioral Pattern Analysis', highlight: false },
                    { text: '2.72M Residential Proxy IPs', highlight: false },
                    { text: '173K Residential CIDR Ranges', highlight: false },
                    { text: 'Live Crowd-Intelligence Blocklist', highlight: false },
                    { text: 'Google / FB / TikTok / MS Ads Bot Detection', highlight: false },
                    { text: 'Fine-Grained Filter Rule Editor', highlight: false },
                    { text: 'A/B Split Testing', highlight: false },
                    { text: 'Priority 24/7 Support', highlight: false }
                  ].map((f) => (
                    <li key={f.text} className="flex items-center gap-3 text-gray-200">
                      <div className="w-5 h-5 rounded-full bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-3.5 h-3.5 text-[#F59E0B]" />
                      </div>
                      <span className={`text-sm ${f.highlight ? 'text-white font-medium' : ''}`}>{f.text}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-auto">
                  <Link to="/pricing" className="block">
                    <Button className="w-full py-6 rounded-xl font-bold bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black hover:shadow-xl hover:shadow-[#F59E0B]/40 transition-all text-base">
                      Get Started Now
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <p className="text-center text-xs text-gray-500 mt-4">30-day money back guarantee</p>
                </div>
              </div>
            </div>

            {/* Professional Plan */}
            <div className="group relative p-8 rounded-3xl bg-gradient-to-b from-purple-500/10 to-purple-500/[0.02] border border-purple-500/20 hover:border-purple-500/40 transition-all duration-500 hover:-translate-y-2 flex flex-col">
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              {/* Badge */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <div className="px-4 py-1.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white text-xs font-bold rounded-full shadow-lg shadow-purple-500/30">
                  Strongest Protection
                </div>
              </div>
              
              <div className="relative pt-2 flex flex-col flex-grow">
                {/* Plan Icon */}
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-600/10 flex items-center justify-center mb-6">
                  <Globe className="w-7 h-7 text-purple-400" />
                </div>
                
                <h3 className="text-2xl font-bold mb-1 text-white">Professional</h3>
                <p className="text-sm text-purple-400/80 mb-6">Enterprise-grade for teams</p>
                
                <div className="flex items-baseline gap-1 mb-8">
                  <span className="text-5xl font-bold text-white">$599</span>
                  <span className="text-gray-500">/mo</span>
                </div>
                
                <ul className="space-y-3 mb-8 flex-grow">
                  {[
                    { text: 'Everything in Personal +', highlight: true },
                    { text: 'Unlimited Campaigns', highlight: true },
                    { text: 'Red-Screen Referrer Detection', highlight: true },
                    { text: 'BrandVerity & AdPolice Shield', highlight: false },
                    { text: 'Competitor-Cloaker Detection (12+)', highlight: false },
                    { text: 'Spy-Service Blocker (AdSpy, AdPlexity, PowerAdSpy, BigSpy +13)', highlight: false },
                    { text: 'Payment-Gateway Cloaking', highlight: false },
                    { text: 'In-App / WebView / PWA Cloaking', highlight: false },
                    { text: 'Crypto-Wallet Visitor Detection', highlight: false },
                    { text: 'Adversarial Automation Detection', highlight: false },
                    { text: '"On-Review" Mode (72h Safe Window)', highlight: false },
                    { text: '72h Post-Approval Paranoid Mode', highlight: false },
                    { text: 'SIVT / MIA Invalid-Traffic Reporting', highlight: false },
                    { text: 'Full Session Recording & Replay', highlight: false },
                    { text: 'Advanced Threat & Anomaly Dashboard', highlight: false },
                    { text: 'Real-Time Telegram Scanner Alerts', highlight: false },
                    { text: 'Full REST API Access', highlight: false },
                    { text: 'Sub-Account Management', highlight: false },
                    { text: 'Guest Campaign Access (Password-Protected)', highlight: false },
                    { text: 'Referral Program (25% Commission)', highlight: false },
                    { text: 'White-Label Branding Options', highlight: false },
                    { text: 'Dedicated Account Manager & SLA', highlight: false }
                  ].map((f) => (
                    <li key={f.text} className="flex items-center gap-3 text-gray-300">
                      <div className="w-5 h-5 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-3.5 h-3.5 text-purple-400" />
                      </div>
                      <span className={`text-sm ${f.highlight ? 'text-white font-medium' : ''}`}>{f.text}</span>
                    </li>
                  ))}
                </ul>
                
                <Link to="/pricing" className="block mt-auto">
                  <Button className="w-full py-6 rounded-xl font-semibold bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white transition-all shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40">
                    Get Started Now
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              <span>Secure Payments</span>
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              <span>30-Day Guarantee</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>10,000+ Users</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              <span>99.9% Uptime</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-28 px-4 relative">
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Questions <span className="bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] bg-clip-text text-transparent">Answered</span>
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div 
                key={i}
                className="border border-white/[0.05] rounded-xl overflow-hidden bg-white/[0.01] hover:bg-white/[0.02] transition-all"
              >
                <button
                  className="w-full p-6 text-left flex items-center justify-between transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-white pr-4">{faq.q}</span>
                  <div className={`w-8 h-8 rounded-full bg-white/[0.05] flex items-center justify-center flex-shrink-0 transition-all ${openFaq === i ? 'bg-[#F59E0B]/20 rotate-45' : ''}`}>
                    <ChevronRight className={`w-4 h-4 transition-transform ${openFaq === i ? 'rotate-45 text-[#F59E0B]' : 'text-gray-500'}`} />
                  </div>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-48' : 'max-h-0'}`}>
                  <div className="px-6 pb-6 text-gray-400 leading-relaxed">
                    {faq.a}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-28 px-4 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F59E0B]/[0.03] to-transparent" />
        
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="relative p-12 md:p-16 rounded-3xl overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#F59E0B]/10 to-transparent" />
            <div className="absolute inset-0 border border-[#F59E0B]/20 rounded-3xl" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#F59E0B]/10 rounded-full blur-[100px]" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#F59E0B]/5 rounded-full blur-[80px]" />
            
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#F59E0B]/10 border border-[#F59E0B]/20 mb-8">
                <Shield className="w-8 h-8 text-[#F59E0B]" />
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Ready to Scale?
              </h2>
              <p className="text-lg text-gray-400 mb-10 max-w-xl mx-auto">
                Join 10,000+ marketers who trust Cloak It Aura to protect their campaigns and maximize ROI
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/pricing">
                  <Button size="lg" className="group bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black font-bold text-lg px-10 py-7 rounded-xl hover:shadow-2xl hover:shadow-[#F59E0B]/30 transition-all">
                    Get Started Now
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/tutorials">
                  <Button size="lg" variant="outline" className="border-white/10 text-white hover:bg-white/5 text-lg px-10 py-7 rounded-xl">
                    View Tutorials
                  </Button>
                </Link>
              </div>
              
              <p className="text-sm text-gray-600 mt-6">Setup in under 5 minutes • No coding required</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 border-t border-white/[0.03] bg-[#020202]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center">
                  <Shield className="w-6 h-6 text-black" />
                </div>
                <span className="text-xl font-bold">Cloak It Aura</span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed max-w-xs mb-6">
                The most advanced ad cloaking platform. Protect your campaigns with VLA™ AI-powered detection.
              </p>
              <div className="flex items-center gap-4 text-2xl text-gray-600">
                <span className="hover:text-[#F59E0B] transition-colors cursor-pointer">₿</span>
                <span className="hover:text-[#F59E0B] transition-colors cursor-pointer">Ξ</span>
                <span className="hover:text-[#F59E0B] transition-colors cursor-pointer">₮</span>
              </div>
            </div>
            
            {/* Product */}
            <div>
              <h4 className="font-semibold mb-5 text-white">Product</h4>
              <ul className="space-y-3">
                <li><a href="#features" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Features</a></li>
                <li><a href="#pricing" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Pricing</a></li>
                <li><Link to="/tutorials" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Tutorials</Link></li>
                <li><Link to="/blog" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Blog</Link></li>
                <li><Link to="/pricing" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Pricing</Link></li>
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h4 className="font-semibold mb-5 text-white">Company</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">About</Link></li>
                <li><Link to="/contact" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Contact</Link></li>
                <li><Link to="/support" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Support</Link></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-5 text-white">Legal</h4>
              <ul className="space-y-3">
                <li><Link to="/privacy" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Terms of Service</Link></li>
                <li><Link to="/refund-policy" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Refund Policy</Link></li>
                <li><Link to="/acceptable-use" className="text-gray-500 hover:text-[#F59E0B] transition-colors text-sm">Acceptable Use</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/[0.03] flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Cloak It Aura. All rights reserved.
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-600">
              <span>VLA™ AI Protection</span>
              <span>•</span>
              <span>7.8B+ IP Database</span>
              <span>•</span>
              <span>21+ Ad Networks</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
